import React, { useEffect, useState, useRef } from 'react';
import { notification, Modal, Button as ANTButton, Popover, Tag } from 'antd';
import { ReloadOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import { useSelector } from 'react-redux';
import './assets/style.css';
import { useQuery } from 'react-query';
import { Breadcrumbs } from '../../components/Atom/breadcumbs';
import { PageHeader } from '../../components/Atom/PageHeader';
import { ResetButton, Button, FilterButton } from '../../components/Atom/Button';
import SearchField from '../../components/Atom/SearchField';
import http from '../../utils/http';
import 'regenerator-runtime';
import { USER_ROLES, TIMEZONES } from 'utils/constant';
import TransactionFilter from './assets/filter';
import { handleDateRangeFormat, checkUserPermissions, convertToStoreTimezone, handleStateByPassData, formatAmount } from 'utils/services';
import StatusTag from 'components/Atom/StatusTag';
import { useLocation, useNavigate } from 'react-router-dom';
import ShortenId from 'components/Atom/ShortenId';
import moment from 'moment';

const Transaction = () => {
  const store = useSelector((state) => state.company.store);
  const [openBulkRefundModel, setOpenBulkRefundModel] = useState(false);
  const [recordId, setRecordId] = useState('');
  const navigate = useNavigate();
  const [delayTimer, setDelayTimer] = useState();
  const location = useLocation();
  const tableContainerRef = useRef(null);
  const hiddenFileInput = useRef(null);
  const [selectedCSVFile, setSelectedCSVFile] = useState(null);
  const [bulkRefundProgress, setBulkRefundProgress] = useState(false);
  const [isInvalidFileSize, setIsInvalidFileSize] = useState(false);
  const [isDefaultRange, setIsDefaultRange] = useState(false);
  const defaultDateApplied = useRef(false);

  const toCapitalized = (str) => {
    if (!str) return '';
    str = str.toString().trim();
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getPaymentGatewayLogo = (item) => {
    const gateway = item?.payment_gateway || item?.payment?.gateway;
    if (!gateway) {
      return 'N/A';
    }

    switch (gateway) {
      case 'bank-alfalah':
      case 'test-bank-alfalah':
      case 'xstak-pay':
        return <img src='https://js.xstak.com/images/bank-alfalah.png' alt='bank-alfalah' />;
      case 'checkout':
        return <img src='https://js.xstak.com/images/checkout.svg' width={100} alt='checkout' />;
      case 'mcb':
        return <img src='https://js.xstak.com/images/MCB.png' width={50} alt='mcb' />;
      case 'hbl-cybersource':
      case 'hbl-cybersource-test':
        return <img src='https://js.xstak.com/images/hbl.png' width={50} alt='hbl-cybersource' />;
      case 'pay-fast':
        return <img src='https://js.xstak.com/images/pay-fast.png' width={50} alt='pay-fast' />;
      case 'safepay-cybersource':
        return <img src='https://js.xstak.com/images/safepay.svg' width={60} alt='safepay-cybersource' />;
      case 'jazzcash-wallet':
        return <img src='https://js.xstak.com/images/jazzcashH.svg' width={50} alt='jazzcash-wallet' />;
      case 'meezan-bank':
      case 'meezan-paypro':
        return <img src='https://js.xstak.com/images/meezan-bank.jpeg' width={40} alt='meezan-bank' />;
      case 'easypaisa-wallet':
        return <img src='https://js.xstak.com/images/easypaisa.svg' width={22} alt='easypaisa-wallet' />;
      case 'baadmay':
        return <img src='https://js.xstak.com/images/baadmay.svg' width={100} alt='baadmay' />;
      case 'test-ubl':
      case 'ubl':
        return <img src='https://js.xstak.com/images/logo/UBL.png' width={40} alt='ubl' />;
      default:
        return gateway?.toUpperCase();
    }
  };

  const columnsData = [
    {
      title: 'Order / Payment Id',
      render: (value) => {
        const shopifyOrderReference = value?.shopify_order_reference || value?.order?.shopifyOrderReference || value?.payment_intent_id?.metadata?.order_reference;
        const orderName = value?.order?.name || value?.order_name;
        return shopifyOrderReference || orderName || '-';
      },
      key: 'order',
    },
    {
      title: 'Email / Phone Number',
      render: (value) => {
        const newValue = value?.order_customer_phone ? value?.order_customer_phone : value?.order_customer_email?.toLowerCase()?.includes('@xpay') ? '-' : value?.order_customer_email;
        const oldValue = value?.order?.customer?.phone ? value?.order?.customer?.phone : value?.order?.customer?.email?.toLowerCase()?.includes('@xpay') ? '-' : value?.order?.customer?.email;
        return oldValue || newValue || '-';
      },
      key: 'email',
    },
    {
      title: 'Amount',
      render: (value) => {
        const currency = value?.payment_currency || value?.payment?.currency;
        const price = value?.payment_price || value?.payment?.price;
        return `${currency} ${formatAmount(price, currency)}`;
      },
      key: 'amount',
    },
    { title: 'Intent Id', render: (value) => <ShortenId value={value?.payment_intent_id?._id ?? value?.payment_intent ?? ''} />, key: 'rule_enabled' },
    {
      title: 'Status',
      render: (value) =>
        (value?.payment_gateway || value?.payment?.gateway) === 'pay-fast' && value?.status?.toUpperCase() === 'OTP_ACTION_REQUIRED' ? (
          <StatusTag tag={'PAYFAST_WEBHOOK_PENDING'} />
        ) : (
          <StatusTag tag={value?.status} />
        ),
      key: 'status',
    },
    {
      title: 'Gateway',
      render: (value) => getPaymentGatewayLogo(value),
      key: 'gateway',
    },
    {
      title: 'Payment Method',
      render: (value) => toCapitalized(value?.payment_method) || toCapitalized(value?.payment_intent_id?.payment_method_types) || 'Card',
      key: 'pm',
    },
    {
      title: 'Time',
      render: (value) => value.updatedAt,
      key: 'time',
    },
  ];

  const [search, setSearch] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [OpenExportModel, setOpenExportModel] = useState(false);
  const exportTransactionDetails = async (data) => {
    setIsDownloading(true);
    try {
      const formattedData = filtersDataValues(data);
      const params = { ...formattedData, ...(store?.isSelected && { store_id: store?._id }) };
      const res = await http.get(`/transactions/export/data`, { params });
      notification.success({ message: res?.data?.data?.message, placement: 'bottomRight' });
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'SOMETHING WENT WRONG', placement: 'bottomRight' });
    }
    setIsDownloading(false);
  };

  const fetchData = async (queryParams) => {
    try {
      const paramsData = { offset: queryParams?.offset, limit: queryParams?.limit };
      if (queryParams?.search !== '') {
        paramsData['search_key'] = queryParams?.search;
      }
      const formattedData = filtersDataValues(queryParams?.filtersData);
      const params = { ...paramsData, ...formattedData, ...(store?.isSelected && { store_id: store?._id }), xap_request: true };
      const response = await http.get(`/transactions/data`, { params });
      const timezone = store?.settings?.timezone || TIMEZONES.PKT;
      const result = await convertToStoreTimezone(response?.data?.data?.docs, timezone);
      response.data.data.docs = result;
      if (response.data.data?.defaultEndDate && response.data.data?.defaultStartDate) {
        setIsDefaultRange(true);
        setQueryParams((prev) => ({
          ...prev,
          filtersData: {
            ...prev.filtersData,
            date_range: [moment(response.data.data?.defaultStartDate), moment(`${response.data.data?.defaultEndDate?.replace(' ', 'T')}Z`)],
          },
        }));
      }
      return response.data.data;
    } catch (err) {
      console.log(err);
    }
  };
  const { data, refetch, isLoading, isFetching } = useQuery('transactions', () => fetchData(queryParams), {
    enabled: false,
    manual: true,
  });
  const [queryParams, setQueryParams] = useState({
    offset: 0,
    limit: 20,
    search: '',
    currentPage: 1,
  });
  const [trigerApi, setTrigerApi] = useState(true);
  useEffect(() => {
    if (location?.state?.isRedirection && data) {
      setSearch(location?.state?.searchValue);
      setRecordId(location?.state?.id);
      navigate(location.pathname, { replace: true });
      const data = handleStateByPassData(location?.state?.filtersData, true);
      if (!data) {
        refetch();
      }
      setQueryParams(data);
      setTimeout(() => {
        if (tableContainerRef?.current && location?.state?.id) {
          const row = document?.querySelector(`.custom-row-class.highlighted-row`);
          if (row) {
            row?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      }, 10);
      return;
    } else if (location?.state?.isRedirection) {
      navigate(location.pathname, { replace: true });
    }
    refetch();
  }, [trigerApi, store]);

  useEffect(() => {
    if (!data || defaultDateApplied.current) return;
    if (queryParams?.filtersData?.date_range) return;
    if (!data.defaultStartDate || !data.defaultEndDate) return;

    defaultDateApplied.current = true;
    setIsDefaultRange(true);
    setQueryParams((prev) => ({
      ...prev,
      filtersData: {
        ...prev.filtersData,
        date_range: [moment(data.defaultStartDate), moment(`${data?.defaultEndDate?.replace(' ', 'T')}Z`)],
      },
    }));
  }, [data, queryParams?.filtersData?.date_range]);

  const filtersDataValues = (data) => {
    let filtersData = {};
    if (data?.date_range) {
      const timezone = store?.isSelected ? store?.settings?.timezone || TIMEZONES.PKT : null;
      const { start_date, end_date } = handleDateRangeFormat(data?.date_range, timezone);
      filtersData.start_date = start_date;
      filtersData.end_date = end_date;
    }
    filtersData.status = data?.transaction_status ?? null;
    filtersData.bins_discount = data?.bins_discount ?? null;
    filtersData.paymentType = data?.paymentType ?? null;
    filtersData.transaction_source = data?.transaction_source ?? null;
    filtersData.gateway = data?.gatewayType ?? null;
    return filtersData;
  };
  const handleFiltersSearch = (data) => {
    if (OpenExportModel) {
      exportTransactionDetails(data);
      setOpenExportModel(false);
    } else {
      setIsDefaultRange(false);
      setQueryParams((pre) => ({ ...pre, currentPage: 1, offset: 0, filtersData: data }));
      setTrigerApi(!trigerApi);
    }
  };
  const getSearch = async (e) => {
    if (e === '' || e == null) {
      notification.error({ message: 'Please enter details for search', placement: 'bottomRight' });
    } else {
      setQueryParams((pre) => ({ ...pre, currentPage: 1, offset: 0, search: e }));
      setTrigerApi(!trigerApi);
    }
  };

  const handlePagePageChange = (page, pageSize) => {
    if (queryParams?.limit !== pageSize) {
      setQueryParams((pre) => ({ ...pre, limit: pageSize, offset: 0, currentPage: 1 }));
      setTrigerApi(!trigerApi);
    } else {
      const countOffset = page * queryParams?.limit - queryParams?.limit;
      setQueryParams((pre) => ({ ...pre, limit: pageSize, offset: countOffset, currentPage: page }));
      setTrigerApi(!trigerApi);
    }
  };
  const handleSearchData = () => {
    if (delayTimer) {
      clearTimeout(delayTimer);
    }
    const newTimer = setTimeout(() => {
      handleRefresh();
    }, 1500);
    setDelayTimer(newTimer);
  };
  const handleRefresh = (isRefresh = true) => {
    if (isRefresh) {
      setSearch(null);
      defaultDateApplied.current = false;
    } else {
      setIsFilterModalOpen(false);
    }
    if (isRefresh) {
      setIsDefaultRange(false);
    }
    setQueryParams((pre) => ({ ...pre, offset: 0, currentPage: 1, search: '', filtersData: isRefresh ? pre?.filtersData : null }));
    setTrigerApi(!trigerApi);
  };
  const handleCloseFilterModel = () => {
    setIsFilterModalOpen(!isFilterModalOpen);
  };

  const [filterToggle, setFilterToggle] = useState(false);
  const filterToggler = () => {
    setFilterToggle(true);
  };
  const handleReset = () => {
    setIsDefaultRange(false);
    setQueryParams((prev) => ({ ...prev, offset: 0, currentPage: 1, filtersData: {} }));
    setTrigerApi(!trigerApi);
    setIsFilterModalOpen(false);
  };
  const popOverContent = (
    <div className='popOver'>
      <div className='popover_header mt-2 mb-3 flex items-center gap-2'>
        <span style={{ fontWeight: '800', color: '#262626', fontSize: '16px' }}>Filter</span>
        <div className='flex items-center text-[#000000] gap-2 cursor-pointer' onClick={() => handleReset()}>
          <ReloadOutlined style={{ fontSize: '16px' }} />
          <span style={{ color: '#000000', fontSize: '14px', lineHeight: '24px' }}>Reset All</span>
        </div>
      </div>
      <TransactionFilter filterToggler={filterToggler} filtersData={queryParams?.filtersData} onSearch={handleFiltersSearch} handleClose={handleCloseFilterModel} isExportFilter={false} />
    </div>
  );
  const breadcrumbProps = {
    breadcrumbs: [
      {
        name: 'XPay',
        href: '/xpay',
      },
      {
        name: 'Transactions',
      },
    ],
  };
  const handleSearch = (e) => {
    if (delayTimer && e !== '' && e !== null) {
      clearTimeout(delayTimer);
    }
    setSearch(e.target.value.trim());
  };
  const handleRowHover = () => {
    setRecordId('');
  };

  // bulk refund file handling
  const handleCSVUploadClick = (event) => {
    hiddenFileInput?.current?.click();
  };
  const handleCSVFileUpload = (e) => {
    const fileData = e?.target?.files[0];
    const fileSizeKB = (fileData?.size / 1024).toFixed(2);
    if (fileData?.type === 'text/csv') {
      setSelectedCSVFile(fileData);
      setIsInvalidFileSize(fileSizeKB > 8 ? true : false);
    } else {
      notification.error({ message: 'Please upload csv file', placement: 'bottomRight' });
    }
  };
  const handleBulkRefund = async () => {
    try {
      setBulkRefundProgress(true);
      const formData = new FormData();
      formData.append('file', selectedCSVFile);
      const resp = await http.post(`/transactions/bulk-refund?store_id=${store?._id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      notification.success({ message: resp?.data?.data?.message || 'Something went wrong', placement: 'bottomRight' });
      setSelectedCSVFile(null);
      setOpenBulkRefundModel(false);
      setBulkRefundProgress(false);
    } catch (err) {
      setBulkRefundProgress(false);
      notification.error({ message: err?.response?.data?.message || 'Something went wrong', placement: 'bottomRight' });
    }
  };
  const handleFileSelectionModel = () => {
    if (store?.isSelected) {
      setOpenBulkRefundModel(true);
    } else {
      notification.error({
        message: 'Store Selection Required',
        description: 'Please select a store from the drop-down menu above to proceed bulk refund',
        placement: 'bottomRight',
      });
    }
  };
  return (
    <div style={{ padding: '0px', width: '100%' }}>
      <div className='header-container'>
        <Breadcrumbs {...breadcrumbProps} />
        <div className='headerPage txn-header-custom'>
          <div className='mb-0'>
            <PageHeader label='Transactions' button={<ResetButton onClick={isFetching ? '' : handleRefresh} loading={isFetching} />} />
          </div>
          <div className='flex txn-btn'>
            {checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_REFUND]) && (
              <Button disabled={bulkRefundProgress} className='custom-btns p-2 flex items-center' onClick={handleFileSelectionModel}>
                <UploadOutlined className='inline text-base' /> <span className='text-base'>Bulk Refund</span>
              </Button>
            )}
            {checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN]) && (
              <Button loading={isDownloading} className='custom-btns p-2 flex items-center' onClick={() => setOpenExportModel(true)}>
                {isDownloading === true ? null : <DownloadOutlined className='inline text-base' />} <span className='text-base'>Export Dump</span>
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className='actionPanel'>
        <div className='headerContainer' style={{ width: '100%' }}>
          <Popover
            content={popOverContent}
            className={isFilterModalOpen ? 'filter-active filterPopover' : filterToggle ? 'text-base activeFilterToggler filterPopover' : 'text-base filterPopover'}
            trigger='click'
            placement='bottomLeft'
            open={isFilterModalOpen}
            destroyTooltipOnHide
            onOpenChange={() => {
              handleCloseFilterModel();
            }}>
            <FilterButton
              onClick={() => {
                setIsFilterModalOpen(true);
              }}>
              <div className='text-base'>Filter</div>
            </FilterButton>
          </Popover>
          <div className='searchInput' style={{ width: '100%' }}>
            <SearchField
              handleOnChange={handleSearch}
              value={search}
              handlePressEnter={getSearch}
              onReset={handleSearchData}
              placeholder={'Search by Transaction ID, Order ID, Intent ID, Email, Phone'}
              loading={!!(isFetching && search)}
              dropdown={{ show: false }}
              enableSearchBtn={true}
            />
          </div>
        </div>
        {/* <div className='child1' style={{ display: 'flex' }}>
          <div className='ml-28 mr-3'>
            <SelectStore onChange={handleOnStoreChange} />
          </div>
        </div> */}
      </div>

      {isDefaultRange && data?.defaultStartDate && data?.defaultEndDate && (
        <div className='mt-2'>
          <Tag>
            <strong className='text-black'>{moment(data?.defaultStartDate).format('MMM D, YYYY h:mm A')}</strong> to{' '}
            <strong className='text-black'>{moment(`${data?.defaultEndDate?.replace(' ', 'T')}Z`).format('MMM D, YYYY h:mm A')}</strong>
          </Tag>
        </div>
      )}
      {!isDefaultRange && queryParams?.filtersData?.date_range?.length === 2 && (
        <div className='mt-2'>
          <Tag>
            <strong className='text-black'> {moment(queryParams.filtersData.date_range[0]).format('MMM D, YYYY h:mm A')}</strong> to{' '}
            <strong className='text-black'>{moment(queryParams.filtersData.date_range[1]).format('MMM D, YYYY h:mm A')}</strong>
          </Tag>
        </div>
      )}
      <br />
      <div className='overflow-x-auto custom-table' ref={tableContainerRef}>
        <Table
          loading={isLoading}
          dataSource={data?.docs}
          columns={columnsData}
          onRow={(record) => {
            return {
              onClick: (event) => {
                const { className } = event?.target;
                if (!className?.includes('table-action-btn')) {
                  navigate(`/transactions/${record?._id}`, { state: { record, id: record?._id, filtersData: handleStateByPassData(queryParams), searchValue: search } });
                }
              },
              onMouseEnter: handleRowHover,
              className: `custom-row-class ${recordId === record?._id ? 'highlighted-row' : ''}`,
            };
          }}
          totalLenght={data?.totalDocs}
          pagingation={{ handlePageChange: handlePagePageChange, perPageSize: queryParams?.limit, page: queryParams?.currentPage }}
        />
      </div>
      <Modal
        trigger='click'
        centered
        width={window.innerWidth < 768 ? 350 : 600}
        open={OpenExportModel}
        footer={null}
        destroyOnClose
        className='exportModalContainer'
        onCancel={() => setOpenExportModel(false)}>
        <div className='font-semibold text-base mb-4'>Export Dump</div>
        <div className='exportModal exportExcelCsvModal'>
          <TransactionFilter filtersData={{}} onSearch={handleFiltersSearch} handleClose={() => {}} isExportFilter={true} />
        </div>
      </Modal>
      <Modal
        trigger='click'
        centered
        width={window.innerWidth < 768 ? 350 : 500}
        open={openBulkRefundModel}
        footer={null}
        destroyOnClose
        className='exportModalContainer bulkExportModal'
        onCancel={() => {
          setSelectedCSVFile(null);
          setOpenBulkRefundModel(false);
        }}>
        <div>
          <div className='font-semibold text-base mb-4'>Bulk Refund</div>
        </div>
        <div className='exportModal exportExcelCsvModal'>
          <ANTButton className='uploadBinsBtn rounded-lg' onClick={handleCSVUploadClick} icon={<UploadOutlined style={{ fontSize: '16px' }} />}>
            Upload CSV File
          </ANTButton>
          <p style={{ marginTop: '5px', marginLeft: '5px', color: selectedCSVFile?.name ? 'blue' : 'gray', ...(!selectedCSVFile?.name && { fontStyle: 'italic' }) }}>
            {selectedCSVFile?.name || 'Maximum allowed size is 8 KB'}
          </p>
          <input type='file' accept='.csv' ref={hiddenFileInput} onChange={handleCSVFileUpload} style={{ display: 'none' }} />
        </div>
        <div style={{ display: 'flex', marginTop: '5px' }}>
          <p>Please download the template file by clicking </p>
          <a href='https://xap.xstak.com/bulk-refund-template-file.csv' target={'_blank'} style={{ color: 'blue', marginLeft: '4px', textDecoration: 'underline', cursor: 'pointer' }}>
            here
          </a>
        </div>
        <div
          className='binClass'
          style={{ display: 'flex', justifyContent: isInvalidFileSize && selectedCSVFile?.name ? 'space-between' : 'end', gap: '5px', alignItems: 'center', marginTop: '16px' }}>
          {isInvalidFileSize && selectedCSVFile?.name && <p style={{ fontStyle: 'italic', color: 'red' }}>File exceeds 8KB.</p>}
          <Button loading={bulkRefundProgress} disabled={!selectedCSVFile?.name || isInvalidFileSize} className='parentThree customExport' onClick={handleBulkRefund}>
            Submit
          </Button>
        </div>
      </Modal>
    </div>
  );
};
export default Transaction;
